import { useCallback, useEffect, useMemo, useState } from 'react';
import { repositoryFactory } from '@data';

const FALLBACK_DELIVERY_MODES = Object.freeze(['log', 'webhook', 'resend', 'postmark', 'smtp', 'disabled']);

const normalizeResponse = (response) => ({
  config: response?.config || {},
  defaults: response?.defaults || {},
  deliveryModes: Array.isArray(response?.deliveryModes)
    ? response.deliveryModes
    : FALLBACK_DELIVERY_MODES,
  hasPersistedOverride: response?.hasPersistedOverride === true,
  createdAt: response?.createdAt || null,
  updatedAt: response?.updatedAt || null,
});

/**
 * Hook for loading and persisting global email delivery configuration.
 */
export function useEmailDeliveryConfiguration(enabled = true) {
  const [configState, setConfigState] = useState(() => normalizeResponse(null));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const courseRepo = useMemo(() => repositoryFactory.getCourseRepository(), []);

  const loadConfiguration = useCallback(async () => {
    if (!enabled) {
      setConfigState(normalizeResponse(null));
      setLoading(false);
      setError(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await courseRepo.getEmailDeliveryConfiguration();
      setConfigState(normalizeResponse(response));
    } catch (err) {
      setError(err.message || 'Failed to load email delivery configuration');
    } finally {
      setLoading(false);
    }
  }, [courseRepo, enabled]);

  useEffect(() => {
    loadConfiguration();
  }, [loadConfiguration]);

  const saveConfiguration = useCallback(async (configData) => {
    if (!enabled) {
      throw new Error('Only administrators can update email configuration');
    }

    try {
      setSaving(true);
      setError(null);

      const response = await courseRepo.upsertEmailDeliveryConfiguration(configData);
      const normalized = normalizeResponse(response);
      setConfigState(normalized);
      return normalized;
    } catch (err) {
      setError(err.message || 'Failed to save email delivery configuration');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [courseRepo, enabled]);

  const resetConfiguration = useCallback(async () => {
    if (!enabled) {
      throw new Error('Only administrators can reset email configuration');
    }

    try {
      setSaving(true);
      setError(null);

      const response = await courseRepo.resetEmailDeliveryConfiguration();
      const normalized = normalizeResponse(response);
      setConfigState(normalized);
      return normalized;
    } catch (err) {
      setError(err.message || 'Failed to reset email delivery configuration');
      throw err;
    } finally {
      setSaving(false);
    }
  }, [courseRepo, enabled]);

  return {
    config: configState.config,
    defaults: configState.defaults,
    deliveryModes: configState.deliveryModes,
    hasPersistedOverride: configState.hasPersistedOverride,
    createdAt: configState.createdAt,
    updatedAt: configState.updatedAt,
    loading,
    saving,
    error,
    refresh: loadConfiguration,
    saveConfiguration,
    resetConfiguration,
    clearError: () => setError(null),
  };
}
